import { useAuth } from "@clerk/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAdvisorSocket } from "../../hooks/useAdvisorSocket";
import {
  fetchActiveAgentConversation,
  seedFromHistory,
} from "../../lib/advisor-history";
import type { ClientFrame } from "../../lib/agent-types";
import type { AdvisorMessage } from "../../lib/advisor-types";
import { useAdvisorDrawerStore, useAdvisorStore } from "../../stores/advisor";

/**
 * AdvisorSessionProvider (plan 435 M6, task asd-session-provider).
 *
 * Owns the ONE WebSocket connection + ONE history hydration for the
 * signed-in user's advisor conversation (decision 528: "one frame, one
 * session"). Before this, every mounted `AdvisorLiveChat` instance ran its
 * own `useAdvisorSocket()` + hydration effect — fine when only one shell
 * (the full chat page) ever existed, but the M6 drawer introduces a SECOND
 * shell over the same conversation, and two independent sockets racing the
 * same `new_conversation` protocol would desync the store.
 *
 * `AdvisorLiveChat` (the full-page shell) becomes presentational over
 * `useAdvisorSession()`: when mounted under an ancestor
 * `<AdvisorSessionProvider>` it reads that shared session; when mounted
 * standalone (its current, only real call site, pending the M6
 * `ConsumerFrame` rollout — asd-consumer-frame) it self-provides an
 * equivalent session scoped to the component instance, eager-opened so the
 * existing standalone behavior stays byte-for-byte unchanged.
 */

export interface AdvisorSession {
  messages: readonly AdvisorMessage[];
  isStreaming: boolean;
  conversationId: string | null;
  /**
   * Sends a client frame over the advisor socket. Flips the lazy
   * connection gate open on first call (question 310: lazy — most sessions
   * under a global `AdvisorSessionProvider` never open Ask Sill, so the
   * socket must not connect merely because the provider mounted).
   */
  send: (frame: ClientFrame) => void;
  /**
   * New conversation via the existing protocol: `send({type:
   * "new_conversation"})` then `clearMessages()` (task asd-new-conversation).
   * No-ops while `isStreaming` — defense in depth; callers should also
   * disable their own "New conversation" control while streaming.
   */
  startNewConversation: () => void;
  /** Resolved Clerk JWT for this session; "" until Clerk resolves one. */
  token: string;
  /**
   * True once the one-shot history hydration for the current token has
   * settled (success or failure) — mirrors the pre-M6 `hydrated` state
   * `AdvisorLiveChat` tracked locally.
   */
  hydrated: boolean;
  /**
   * Plan 435 M6 integration pass (asd-consumer-frame hazard fix). Marks
   * that THIS mount is about to overwrite the conversation with a fresh
   * `new_conversation` + submit (e.g. a `?view=advisor` deep link with
   * `initialPromptAction="submit_new"`), so the session's own one-shot
   * hydration — which under a SHARED `AdvisorSessionProvider` runs
   * independently of any one shell's intent — must not clobber it if its
   * fetch resolves late. Call this BEFORE `clearMessages()`/`send()`; the
   * hydration effect checks the flag at fetch-resolution time (not
   * schedule time), so call order relative to the hydration fetch starting
   * doesn't matter — only relative to when that fetch's `.then` runs,
   * which is always after every synchronous effect in the same commit has
   * run. Idempotent; a no-op once hydration has already settled.
   */
  requestFreshStart: () => void;
}

export const AdvisorSessionContext = createContext<AdvisorSession | null>(
  null,
);

/**
 * The real session implementation, parameterized so one component can
 * either HOST the shared session (`AdvisorSessionProvider`, `active:
 * true`, `eager: false` — lazy) or SELF-PROVIDE a standalone one when no
 * ancestor provider exists (`active`/`eager` both tied to "no context
 * found", eager so the standalone call site's socket still opens as soon
 * as a token is available, exactly like the pre-M6 behavior).
 *
 * When `active` is false every effect below is a no-op and the returned
 * object is never read by its caller (`useAdvisorSession` prefers the
 * ancestor context) — calling this hook unconditionally keeps the rules of
 * hooks intact without paying for a second live connection.
 */
function useAdvisorSessionInternal({
  active,
  eager,
  suppressHydration = false,
}: {
  active: boolean;
  eager: boolean;
  /**
   * Skip the hydration FETCH (but still mark `hydrated: true` once a token
   * is available) for a mount that is about to blow away history with its
   * own fresh `new_conversation` anyway — e.g. `AdvisorLiveChat`'s
   * `initialPromptAction="submit_new"` call site. Only meaningful for the
   * standalone fallback; a shared provider session has no notion of a
   * particular consumer's "about to submit" intent.
   */
  suppressHydration?: boolean;
}): AdvisorSession {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [token, setToken] = useState("");
  const [hydrated, setHydrated] = useState(false);
  // Lazy-open gate (question 310 default): the socket connects only once
  // something actually requests it — `send()`/`startNewConversation()` call
  // `ensureOpen()` below. The standalone fallback starts already open
  // (`eager`) since its one call site (`AdvisorLiveChat`, mounted only
  // while the user is literally viewing the chat page) IS the "first use"
  // signal; opening on that mount preserves the pre-M6 behavior exactly.
  const [sessionOpen, setSessionOpen] = useState(eager);
  // See `AdvisorSession.requestFreshStart`'s doc comment. Reset on sign-out
  // below alongside `hydrated`/`token` — a fresh sign-in starts clean.
  const freshStartRequestedRef = useRef(false);

  const messages = useAdvisorStore((s) => s.messages);
  const isStreaming = useAdvisorStore((s) => s.isStreaming);
  const conversationId = useAdvisorStore((s) => s.conversationId);
  const loadHistory = useAdvisorStore((s) => s.loadHistory);
  const clearMessages = useAdvisorStore((s) => s.clearMessages);

  // Token acquisition — one resolve per sign-in. `getToken` is Clerk's
  // stable resolver; a background JWT refresh does not re-run this effect,
  // matching the pre-M6 behavior (the socket re-resolves a fresh token on
  // every reconnect attempt via useAdvisorSocket's own getToken ref).
  useEffect(() => {
    if (!active) return;
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    getToken()
      .then((next) => {
        if (!cancelled && next) setToken(next);
      })
      .catch(() => {
        // Leave token empty; the hook stays disconnected until we succeed.
      });
    return () => {
      cancelled = true;
    };
  }, [active, getToken, isLoaded, isSignedIn]);

  // Sign-out teardown: Clerk requires a sign-out before a different
  // identity can sign in, so "token change" and "sign-out" are the same
  // transition here. Closes the socket (by clearing `token`, which
  // disables `useAdvisorSocket` below) and clears the shared store exactly
  // once per sign-out — a later sign-in re-runs the acquisition effect
  // above and re-hydrates fresh.
  const wasSignedInRef = useRef(isSignedIn);
  useEffect(() => {
    if (!active) return;
    if (wasSignedInRef.current && !isSignedIn) {
      setToken("");
      setHydrated(false);
      setSessionOpen(eager);
      freshStartRequestedRef.current = false;
      clearMessages();
      // Question 308 default: the drawer resets on sign-out (persists
      // across in-app navigation otherwise). Also drops any active entity
      // context — a stale offer/agent id must not survive into the next
      // signed-in session.
      useAdvisorDrawerStore.getState().closeDrawer();
    }
    wasSignedInRef.current = isSignedIn;
  }, [active, isSignedIn, clearMessages, eager]);

  // One-shot hydration per token: fetch the user's active conversation,
  // seed messages + dock items, then mark hydrated. Independent of the
  // lazy socket gate — history hydration is a plain REST read.
  useEffect(() => {
    if (!active || !token || hydrated) return;
    if (suppressHydration) {
      // queueMicrotask (not a bare setState call) sidesteps the
      // react-hooks/set-state-in-effect lint rule the same way the pre-M6
      // `advisor-live-chat.tsx` did for this exact "mark hydrated without
      // fetching" case.
      queueMicrotask(() => setHydrated(true));
      return;
    }
    const controller = new AbortController();
    fetchActiveAgentConversation(token, undefined, controller.signal)
      .then((res) => {
        // A fresh-start was requested (synchronously, during a render's
        // effect pass) sometime between this fetch starting and resolving —
        // the conversation it would seed is already stale. Mark hydrated
        // without touching the store so the caller that requested the
        // fresh start owns what the store contains.
        if (!freshStartRequestedRef.current) {
          loadHistory(seedFromHistory(res));
        }
        setHydrated(true);
      })
      .catch(() => {
        setHydrated(true);
      });
    return () => {
      controller.abort();
    };
  }, [active, token, hydrated, loadHistory, suppressHydration]);

  const ensureOpen = useCallback(() => {
    setSessionOpen(true);
  }, []);

  const requestFreshStart = useCallback(() => {
    freshStartRequestedRef.current = true;
  }, []);

  const { send: socketSend } = useAdvisorSocket({
    getToken,
    enabled: active && token !== "" && sessionOpen,
  });

  const send = useCallback(
    (frame: ClientFrame) => {
      ensureOpen();
      socketSend(frame);
    },
    [ensureOpen, socketSend],
  );

  const startNewConversation = useCallback(() => {
    if (useAdvisorStore.getState().isStreaming) return;
    send({ type: "new_conversation" });
    clearMessages();
    // Scenario: "closing the drawer or starting a new conversation clears
    // [the active context]" — a fresh conversation should not inherit the
    // prior turn's entity context.
    useAdvisorDrawerStore.getState().setActiveContext(null);
  }, [send, clearMessages]);

  return {
    messages,
    isStreaming,
    conversationId,
    send,
    startNewConversation,
    token,
    hydrated,
    requestFreshStart,
  };
}

export function AdvisorSessionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const session = useAdvisorSessionInternal({ active: true, eager: false });
  return (
    <AdvisorSessionContext.Provider value={session}>
      {children}
    </AdvisorSessionContext.Provider>
  );
}

/**
 * Returns the ancestor `AdvisorSessionProvider`'s session when mounted
 * inside one. Otherwise self-provides an equivalent, eager-opened session
 * scoped to this component instance — see the module doc comment.
 *
 * `suppressHydration` only affects the standalone fallback (see
 * `useAdvisorSessionInternal`'s doc comment) and is silently ignored when
 * an ancestor provider's shared session is in play.
 */
export function useAdvisorSession(options?: {
  suppressHydration?: boolean;
}): AdvisorSession {
  const ctx = useContext(AdvisorSessionContext);
  const standalone = useAdvisorSessionInternal({
    active: ctx === null,
    eager: ctx === null,
    suppressHydration: ctx === null && (options?.suppressHydration ?? false),
  });
  return ctx ?? standalone;
}
